import React from "react";
import Button from "../components/Button.jsx";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Plus, Save, Shield, User } from "lucide-react";
import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import Message from "../components/Message.jsx";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext.jsx";
import { track } from "@crm/analytics";

export function DialogNewUser({ onSuccess, defaultRole = "user" }) {
    const { t } = useTranslation();
    const { numberOfUsers, currentUserCount, userLimitReached, getUser, isFeatureEnabled } =
        useAuth();
    const hasComplianceOfficer = isFeatureEnabled("complianceOfficer");
    const [formData, setFormData] = React.useState({
        email: "",
        firstName: "",
        lastName: "",
        role: defaultRole, // Default role
    });
    const [errors, setErrors] = React.useState({});
    const [open, setOpen] = React.useState(false);

    const { call, error, isLoading } = useBluelagoon({
        method: "createUser",
    });

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email) newErrors.email = t("newUser.emailRequired");
        if (!formData.firstName)
            newErrors.firstName = t("newUser.firstNameRequired");
        if (!formData.lastName)
            newErrors.lastName = t("newUser.lastNameRequired");
        if (!formData.role) newErrors.role = t("newUser.roleRequired");

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // Check user limit before creating
        if (userLimitReached && !Bluelagoon.isDevelopment && !Bluelagoon.isStaging) {
            setErrors({
                ...errors,
                general: `Cannot add more users. Your plan allows maximum ${numberOfUsers} users and you currently have ${currentUserCount}.`,
            });
            return;
        }

        try {
            await call(formData);

            // Track new user added
            track("New User Added", {
                role: formData.role,
                email: formData.email,
            });

            await getUser();
            setOpen(false);
            setFormData({
                email: "",
                firstName: "",
                lastName: "",
                role: "user",
            });
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Error creating user:", err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear errors when user types
        if (errors[name] || errors.general) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
                general: undefined,
            }));
        }
    };

    const handleRoleChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            role: value,
        }));
        // Clear errors when user selects
        if (errors.role || errors.general) {
            setErrors((prev) => ({
                ...prev,
                role: undefined,
                general: undefined,
            }));
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case "admin":
                return <Shield className="w-4 h-4 text-red-500" />;
            case "creator":
                return <User className="w-4 h-4 text-blue-500" />;
            case "client":
                return <User className="w-4 h-4 text-green-500" />;
            case "compliance-officer":
                return <Shield className="w-4 h-4 text-purple-500" />;
            default:
                return <User className="w-4 h-4 text-gray-500" />;
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button>
                    <Plus className="mr-2" />
                    {t("newUser.newUser")}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("newUser.createNewUser")}</DialogTitle>
                    <DialogDescription>
                        {t("newUser.addNewUserDescription")}
                        <div className="mt-2 text-sm text-muted-foreground">
                            Users: {currentUserCount} / {numberOfUsers}
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">{t("newUser.email")}</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder={t("newUser.emailPlaceholder")}
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                        {errors.email && (
                            <p className="text-red-500 text-sm">
                                {errors.email}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="firstName">
                            {t("newUser.firstName")}
                        </Label>
                        <Input
                            id="firstName"
                            name="firstName"
                            placeholder={t("newUser.firstNamePlaceholder")}
                            value={formData.firstName}
                            onChange={handleInputChange}
                        />
                        {errors.firstName && (
                            <p className="text-red-500 text-sm">
                                {errors.firstName}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lastName">
                            {t("newUser.lastName")}
                        </Label>
                        <Input
                            id="lastName"
                            name="lastName"
                            placeholder={t("newUser.lastNamePlaceholder")}
                            value={formData.lastName}
                            onChange={handleInputChange}
                        />
                        {errors.lastName && (
                            <p className="text-red-500 text-sm">
                                {errors.lastName}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="role">{t("newUser.role")}</Label>
                        <Select
                            value={formData.role}
                            onValueChange={handleRoleChange}
                        >
                            <SelectTrigger>
                                <div className="flex items-center gap-2">
                                    {getRoleIcon(formData.role)}
                                    <SelectValue
                                        placeholder={t(
                                            "newUser.selectRolePlaceholder"
                                        )}
                                    />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">
                                    <div className="flex items-center gap-2">
                                        {t("users.admin")}
                                    </div>
                                </SelectItem>
                                <SelectItem value="creator">
                                    <div className="flex items-center gap-2">
                                        {t("users.creator")}
                                    </div>
                                </SelectItem>
                                <SelectItem value="client">
                                    <div className="flex items-center gap-2">
                                        {t("newUser.clientViewer")}
                                    </div>
                                </SelectItem>
                                {hasComplianceOfficer && (
                                    <SelectItem value="compliance-officer">
                                        <div className="flex items-center gap-2">
                                            {t("users.complianceOfficer")}
                                        </div>
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {errors.role && (
                            <p className="text-red-500 text-sm">
                                {errors.role}
                            </p>
                        )}
                    </div>

                    {errors.general && (
                        <div className="text-red-500 text-sm">
                            {errors.general}
                        </div>
                    )}

                    {error && (
                        <Message className="text-red-500" error>
                            {error?.message}
                        </Message>
                    )}

                    <DialogFooter className="gap-2">
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                {t("users.cancel")}
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            isLoading={isLoading ? "true" : undefined}
                        >
                            <Save className="mr-2" />
                            {t("newUser.createUser")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
